/** @forge-header
 * @id          tennisParsers
 * @role        Tennis ticker parsers | ATP + WTA match parsing
 * @pattern     pure-fn
 * @purity      none
 * @units       n/a
 * @upstream    kalshiTicker/types:ParsedTicker,TennisMatchInfo | kalshiTicker/helpers:parseFallback,formatTennisPlayerCode
 * @downstream  kalshiTicker/parse
 * @exports     parseAtpMatch:fn | parseWtaMatch:fn
 * @test        parse.test.ts (indirect)
 */

import type { ParsedTicker, TennisMatchInfo } from '../types';
import { parseDateFromTicker } from '../types';
import { parseFallback, formatTennisPlayerCode } from '../helpers';

/** ATP tennis match: KXATPMATCH-26FEB08DJOSINNER-DJO */
export function parseAtpMatch(raw: string): ParsedTicker {
  return parseTennisMatchGeneric(raw, 'KXATPMATCH', 'ATP');
}

/** WTA tennis match: KXWTAMATCH-26FEB08SWIGAUFF-SWI */
export function parseWtaMatch(raw: string): ParsedTicker {
  return parseTennisMatchGeneric(raw, 'KXWTAMATCH', 'WTA');
}

/** ITF men's match: KXITFMATCH-26MAY27STRDEA-STR (folded into the ATP tab) */
export function parseItfMatch(raw: string): ParsedTicker {
  return parseTennisMatchGeneric(raw, 'KXITFMATCH', 'ITF');
}

/** ITF women's match: KXITFWMATCH-26MAY27YAMOSU-YAM (folded into the WTA tab) */
export function parseItfwMatch(raw: string): ParsedTicker {
  return parseTennisMatchGeneric(raw, 'KXITFWMATCH', 'ITF');
}

/** Generic tennis match parser */
function parseTennisMatchGeneric(raw: string, series: string, label: string): ParsedTicker {
  const parts = raw.split('-');
  if (parts.length < 2) {
    return parseFallback(raw);
  }

  const mid = parts[1] ?? '';
  if (mid.length < 13) {
    return parseFallback(raw);
  }

  const date = parseDateFromTicker(mid.slice(0, 7));
  const playersStr = mid.slice(7);

  const outcome = parts[2] ?? null;

  const tennis: TennisMatchInfo = {
    player1: {
      playerCode: playersStr.slice(0, Math.floor(playersStr.length / 2)),
      displayName: formatTennisPlayerCode(playersStr.slice(0, Math.floor(playersStr.length / 2))),
    },
    player2: {
      playerCode: playersStr.slice(Math.floor(playersStr.length / 2)),
      displayName: formatTennisPlayerCode(playersStr.slice(Math.floor(playersStr.length / 2))),
    },
  };

  const shortLabel = outcome
    ? `${label}: ${formatTennisPlayerCode(outcome)} Win`
    : `${label} Match`;

  const fullLabel = `${label}: ${tennis.player1.displayName} vs ${tennis.player2.displayName}${outcome ? ` · ${formatTennisPlayerCode(outcome)} Win` : ''}`;

  return {
    raw,
    category: 'tennis-match',
    series,
    date,
    game: null,
    player: null,
    spread: null,
    total: null,
    tennis,
    outcome,
    shortLabel,
    fullLabel,
  };
}
